import React, { useMemo, useState } from "react";
import { useParams, Outlet, Link, useLocation } from "@tanstack/react-router";
import { FileSpreadsheet, CheckSquare, List, Book } from "lucide-react";
import { cn } from "@/lib/utils";
import { EMS_TAB_BAR, emsTabClass } from "@/components/ui/ems-tabs";
import { ModuleTabsContext } from "@/layouts/ModuleTabsContext";
import { resolveWarehouseDomain, WAREHOUSE_DOMAINS } from "@apv-erp/status";

/**
 * Khung chung của một kho (vật tư / thành phẩm / kỹ thuật / BTP / phế liệu): mỗi kho đủ 4 nghiệp vụ
 * Phiếu nhập xuất, Báo cáo NXT, Sổ kho, Kiểm kê (mỗi màn có In / PDF và Xuất Excel).
 */
export function WarehouseTypeModule() {
  const { warehouseType } = useParams({ strict: false }) as { warehouseType: string };
  const location = useLocation();
  const domain = resolveWarehouseDomain(warehouseType) || "MATERIAL";
  const def = WAREHOUSE_DOMAINS[domain];
  // Trang xem / form chứng từ (StandardDetailLayout) báo lên để bỏ lề: tiêu đề chứng từ nằm sát dưới thanh tab
  const [isDocPage, setDocShell] = useState(false);
  const moduleTabs = useMemo(() => ({ setDocShell }), []);

  const navItems = [
    { name: "Phiếu nhập xuất", href: `/warehouse/${warehouseType}/transactions`, icon: List, exact: false },
    { name: "Báo cáo NXT", href: `/warehouse/${warehouseType}`, icon: FileSpreadsheet, exact: true },
    { name: "Sổ kho", href: `/warehouse/${warehouseType}/stock-card`, icon: Book, exact: false },
    { name: "Kiểm kê", href: `/warehouse/${warehouseType}/stocktakes`, icon: CheckSquare, exact: false },
  ];

  return (
    <div className="flex h-full flex-col">
      {/* Tab module chuẩn EMS (thẻ + gạch chân đậm). Tên kho đã ở thanh trên cùng, không lặp lại ở đây. */}
      <nav
        aria-label={def.label}
        data-testid="warehouse-module-tabs"
        className={cn(EMS_TAB_BAR, "bg-white px-4 pt-1.5 z-10 overflow-x-auto no-scrollbar shrink-0")}
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.exact
            ? location.pathname === item.href
            : location.pathname.startsWith(item.href);

          return (
            <Link
              key={item.href}
              to={item.href}
              aria-current={isActive ? "page" : undefined}
              className={emsTabClass(isActive)}
            >
              <Icon className={cn("h-4 w-4", isActive ? "text-primary" : "text-slate-400")} />
              {item.name}
            </Link>
          );
        })}
      </nav>

      {/* Trang danh sách (ListPageShell) lấp đầy phần còn lại: phân trang luôn ở đáy, tiêu đề bảng đứng yên khi cuộn.
          Trang xem / form: tiêu đề chứng từ nằm sát dưới thanh tab (không lề), tự có lề nội dung. */}
      <div
        className={cn(
          "flex-1 min-h-0 overflow-auto bg-slate-50",
          isDocPage ? "" : "p-6 has-[.erp-list-shell]:p-0 has-[.erp-list-shell]:overflow-hidden [&_.erp-list-shell]:!h-full"
        )}
      >
        <ModuleTabsContext.Provider value={moduleTabs}>
          <Outlet />
        </ModuleTabsContext.Provider>
      </div>
    </div>
  );
}
